import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  Typography,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SavingsRounded from "@mui/icons-material/SavingsRounded";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import ReceiptLongRounded from "@mui/icons-material/ReceiptLongRounded";
import ShoppingBagRounded from "@mui/icons-material/ShoppingBagRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { firestore as db } from "firebaseConfig";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useMaterialUIController } from "context";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "examples/FormatCurrency";
import AddRounded from "@mui/icons-material/AddRounded";
import MoreHoriz from "@mui/icons-material/MoreHoriz";

// Função para formatar valores grandes (ex.: 42K)
const formatLargeNumber = (value) => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}K`;
  }
  return value.toLocaleString("pt-BR");
};

// Função para calcular o percentual de variação
const calculateComparativePercentage = (current, previous) => {
  if (!previous || previous === 0) {
    return current > 0 ? 100 : 0;
  }
  const percentage = ((current - previous) / Math.abs(previous)) * 100;
  return Math.round(percentage);
};

// Função para formatar o texto do comparativo com cores e setas corretas
const formatComparativoTexto = (valorMesAtual, valorMesAnterior, darkMode, currency) => {
  const comparativoPercentual = calculateComparativePercentage(valorMesAtual || 0, valorMesAnterior || 0);
  const isPositive = comparativoPercentual >= 0; // Positivo ou igual a zero usa seta para cima e verde
  const triangleStyle = {
    width: 0,
    height: 0,
    borderLeft: "5px solid transparent",
    borderRight: "5px solid transparent",
    borderBottom: isPositive ? "8px solid #4CAF50" : "0", // Seta para baixo (verde) se positivo, none se negativo
    borderTop: isPositive ? "0" : "8px solid #EF5350", // Seta para cima (vermelho) se negativo
    marginRight: "4px",
  };
  const textColor = isPositive ? (darkMode ? "#81C784" : "#4CAF50") : darkMode ? "#EF9A9A" : "#EF5350";

  return (
    <MDBox display="flex" alignItems="center" gap={1}>
      <div style={triangleStyle} />
      <Typography variant="body2" color={textColor} fontSize={{ xs: "0.8rem", sm: "0.9rem" }}>
        {Math.abs(comparativoPercentual)}% este mês
      </Typography>
    </MDBox>
  );
};

// Função para gerar rótulos do eixo Y (0 no topo, maior valor na base)
const generateYAxisLabels = (maxValue) => {
  const steps = 4;
  const stepValue = maxValue / steps;
  const labels = [];
  for (let i = steps; i >= 0; i--) {
    labels.push(formatLargeNumber(i * stepValue));
  }
  return labels;
};

// Função para calcular o conic-gradient e os ângulos para o SVG
const calculateConicGradient = (gastos) => {
  const total = gastos.reduce((acc, gasto) => acc + gasto.amount, 0);
  let gradient = "";
  let currentPercentage = 0;
  const sections = [];

  gastos.forEach((gasto, index) => {
    const percentage = (gasto.amount / total) * 100;
    const start = currentPercentage;
    const end = currentPercentage + percentage;
    const color =
      index === 0
        ? "#F57C00"
        : index === 1
        ? "#8E24AA"
        : index === 2
        ? "#AB47BC"
        : index === 3
        ? "#4CAF50"
        : index === 4
        ? "#FF9800"
        : "#7B1FA2";
    gradient += `${color} ${start}% ${end}%, `;
    sections.push({
      category: gasto.category,
      percentage: percentage.toFixed(1),
      startAngle: (start / 100) * 360,
      endAngle: (end / 100) * 360,
      color,
    });
    currentPercentage = end;
  });

  return { gradient: gradient.slice(0, -2), sections };
};

// Função para calcular o caminho SVG de um segmento de anel
const describeArc = (x, y, outerRadius, innerRadius, startAngle, endAngle) => {
  const startOuter = polarToCartesian(x, y, outerRadius, startAngle);
  const endOuter = polarToCartesian(x, y, outerRadius, endAngle);
  const startInner = polarToCartesian(x, y, innerRadius, startAngle);
  const endInner = polarToCartesian(x, y, innerRadius, endAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  const d = [
    "M",
    startOuter.x,
    startOuter.y,
    "A",
    outerRadius,
    outerRadius,
    0,
    largeArcFlag,
    1,
    endOuter.x,
    endOuter.y,
    "L",
    endInner.x,
    endInner.y,
    "A",
    innerRadius,
    innerRadius,
    0,
    largeArcFlag,
    0,
    startInner.x,
    startInner.y,
    "Z",
  ].join(" ");

  return d;
};

// Converte ângulos polares para coordenadas cartesianas
const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

function Dashboard() {
  const [saldo, setSaldo] = useState(0);
  const [receitas, setReceitas] = useState(0);
  const [despesasFixas, setDespesasFixas] = useState(0);
  const [despesasVariaveis, setDespesasVariaveis] = useState(0);

  const [monthlyReceitas, setMonthlyReceitas] = useState(Array(12).fill(0));
  const [monthlyDespesasFixas, setMonthlyDespesasFixas] = useState(Array(12).fill(0));
  const [monthlyDespesasVariaveis, setMonthlyDespesasVariaveis] = useState(Array(12).fill(0));

  const [pendentes, setPendentes] = useState([]);
  const [gastosCategoria, setGastosCategoria] = useState([]);
  const [userId, setUserId] = useState(null);

  const [controller] = useMaterialUIController();
  const { darkMode, currency } = controller;
  const currentMonth = new Date().getMonth();
  const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;

  const navigate = useNavigate();

  const [isGastosModalOpen, setIsGastosModalOpen] = useState(false);
  const [hoveredSection, setHoveredSection] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchMonthlyData();
      fetchPendentes();
      fetchGastosCategoria();
    }
  }, [userId, currency]);

  const getMonthFromDate = (dateString) => {
    const [day, month, year] = dateString.split("/").map(Number);
    return month - 1;
  };

  const parseCurrency = (valueString) => {
    if (!valueString) return 0;

    if (typeof valueString === "number") return valueString;

    let cleanedValue = valueString.toString().replace(/[^0-9,.]/g, "");

    console.log("parseCurrency input:", valueString, "cleaned:", cleanedValue);

    if (cleanedValue.includes(",") && cleanedValue.includes(".")) {
      cleanedValue = cleanedValue.replace(/\./g, "");
      cleanedValue = cleanedValue.replace(",", ".");
    } else if (cleanedValue.includes(",") && !cleanedValue.includes(".")) {
      cleanedValue = cleanedValue.replace(",", ".");
    } else if (cleanedValue.includes(".") && !cleanedValue.includes(",")) {
    } else if (cleanedValue.includes(",")) {
      const parts = cleanedValue.split(",");
      const lastPart = parts.pop();
      const integerPart = parts.join("");
      cleanedValue = `${integerPart}.${lastPart}`;
    }

    const result = parseFloat(cleanedValue) || 0;
    console.log("parseCurrency result:", result);
    return result;
  };

  const fetchMonthlyData = async () => {
    const receitas = Array(12).fill(0);
    const despesasFixas = Array(12).fill(0);
    const despesasVariaveis = Array(12).fill(0);

    try {
      const lancamentosRef = collection(db, "usuarios", userId, "lancamentos");
      const querySnapshot = await getDocs(query(lancamentosRef, where("pago", "==", true)));

      querySnapshot.forEach((doc) => {
        const data = doc.data() || {};
        const month = data.data ? getMonthFromDate(data.data) : null;
        const amount = data.valor ? parseCurrency(data.valor) : 0;

        if (month !== null && data.tipo) {
          if (data.tipo === "Receita") receitas[month] += amount;
          if (data.tipo === "Despesa Fixa") despesasFixas[month] += amount;
          if (data.tipo === "Despesa Variável") despesasVariaveis[month] += amount;
        }
      });

      setReceitas(receitas.reduce((acc, val) => acc + val, 0));
      setDespesasFixas(despesasFixas.reduce((acc, val) => acc + val, 0));
      setDespesasVariaveis(despesasVariaveis.reduce((acc, val) => acc + val, 0));
      setSaldo(
        receitas.reduce((acc, val) => acc + val, 0) -
          despesasFixas.reduce((acc, val) => acc + val, 0) -
          despesasVariaveis.reduce((acc, val) => acc + val, 0)
      );

      setMonthlyReceitas(receitas);
      setMonthlyDespesasFixas(despesasFixas);
      setMonthlyDespesasVariaveis(despesasVariaveis);
    } catch (error) {
      console.error("Erro ao buscar dados mensais:", error);
    }
  };

  const [pendentesResumo, setPendentesResumo] = useState({
    vencidos: 0,
    aVencerHoje: 0,
    proximos7Dias: 0,
  });
  const [pendentesDetalhes, setPendentesDetalhes] = useState([]);
  const [isResumoModalOpen, setIsResumoModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const handleOpenTransactionModal = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleCloseTransactionModal = () => {
    setSelectedTransaction(null);
    setIsModalOpen(false);
    fetchPendentes();
  };

  const handleMarkAsPaid = async (transactionId) => {
    try {
      const transactionRef = doc(db, "usuarios", userId, "lancamentos", transactionId);
      await updateDoc(transactionRef, { pago: true });

      setPendentesDetalhes((prevPendentes) => prevPendentes.filter((transacao) => transacao.id !== transactionId));

      setIsResumoModalOpen(false);
      fetchPendentes();
    } catch (error) {
      console.error("Erro ao marcar transação como paga:", error);
    }
  };

  const fetchPendentes = async () => {
    try {
      const lancamentosRef = collection(db, "usuarios", userId, "lancamentos");
      const querySnapshot = await getDocs(query(lancamentosRef, where("pago", "==", false)));

      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      const proximos7Dias = new Date();
      proximos7Dias.setDate(hoje.getDate() + 7);
      proximos7Dias.setHours(0, 0, 0, 0);

      let vencidos = 0;
      let aVencerHoje = 0;
      let proximos7DiasContagem = 0;
      const detalhes = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data() || {};
        const dataField = data.data || "";

        try {
          const dataLancamento = new Date(dataField.split("/").reverse().join("-"));
          dataLancamento.setMinutes(dataLancamento.getMinutes() + dataLancamento.getTimezoneOffset());
          dataLancamento.setHours(0, 0, 0, 0);

          if (dataLancamento < hoje) {
            vencidos++;
          } else if (dataLancamento.getTime() === hoje.getTime()) {
            aVencerHoje++;
          } else if (dataLancamento > hoje && dataLancamento <= proximos7Dias) {
            proximos7DiasContagem++;
          }

          detalhes.push({
            id: doc.id,
            description: data.categoria || "Descrição não disponível",
            date: dataField,
            amount: data.valor || "0",
            paymentMethod: data.formaPagamento || "Não especificado",
            observation: data.descricao || "Nenhuma observação",
            emoji: data.emoji || "📅",
          });
        } catch (err) {
          console.warn(`Erro ao processar o documento ${doc.id}:`, err);
        }
      });

      setPendentesResumo({ vencidos, aVencerHoje, proximos7Dias: proximos7DiasContagem });
      setPendentesDetalhes(detalhes);
    } catch (error) {
      console.error("Erro ao buscar transações pendentes:", error);
    }
  };

  const fetchCategorias = async () => {
    try {
      const categoriasRef = collection(db, "usuarios", userId, "categorias");
      const snapshot = await getDocs(categoriasRef);
      const categoriasMap = {};
      snapshot.forEach((doc) => {
        const data = doc.data();
        categoriasMap[data.nome] = { emoji: data.emoji || "❗", color: data.color || "#000" };
      });
      return categoriasMap;
    } catch (error) {
      console.error("Erro ao buscar categorias:", error);
      return {};
    }
  };

  const fetchGastosCategoria = async () => {
    try {
      const categoriasMap = await fetchCategorias();
      const lancamentosRef = collection(db, "usuarios", userId, "lancamentos");
      const querySnapshot = await getDocs(query(lancamentosRef, where("pago", "==", true)));

      const categoriaGastos = {};
      querySnapshot.forEach((doc) => {
        const data = doc.data() || {};
        const category = data.categoria || "Outros";
        const amount = data.valor ? parseCurrency(data.valor) : 0;
        if (data.tipo === "Despesa Fixa" || data.tipo === "Despesa Variável") {
          categoriaGastos[category] = {
            amount: (categoriaGastos[category]?.amount || 0) + amount,
            emoji: categoriasMap[category]?.emoji || "❗",
            color: categoriasMap[category]?.color || "#000",
          };
        }
      });

      const sortedGastos = Object.entries(categoriaGastos)
        .map(([category, { amount, emoji, color }]) => ({ category, amount, emoji, color }))
        .sort((a, b) => b.amount - a.amount);
      setGastosCategoria(sortedGastos);
    } catch (error) {
      console.error("Erro ao buscar gastos por categoria:", error);
    }
  };

  const meses = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

  const maxAmount = Math.max(...gastosCategoria.map((gasto) => gasto.amount), 1);

  if (!userId) {
    return <Typography variant="h6">Carregando dados...</Typography>;
  }

  const { gradient, sections } = calculateConicGradient(gastosCategoria);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Cards de Resumo */}
        <Grid container spacing={3}>
          <Grid item xs={6} sm={6} md={3}>
            <Card
              sx={{
                background: darkMode
                  ? "linear-gradient(135deg, #424242, #616161)"
                  : "linear-gradient(135deg, #E8EAF6, #CFD8DC)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography
                  variant="body1"
                  color={darkMode ? "#757575" : "#455A64"}
                  fontSize={{ xs: "0.788rem", sm: "1rem" }}
                >
                  Saldo
                </Typography>
                <MDBox
                  sx={{
                    backgroundColor: darkMode ? "#616161" : "#CFD8DC",
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SavingsRounded sx={{ fontSize: 16, color: darkMode ? "#A9A9A9" : "#5A5A5A" }} />
                </MDBox>
              </MDBox>
              <Typography
                variant="h5"
                fontWeight="bold"
                color={darkMode ? "#B0BEC5" : "#263238"}
                fontSize={{ xs: "1.125rem", sm: "1.5rem" }}
              >
                {formatCurrency(
                  monthlyReceitas.reduce((acc, val) => acc + val, 0) -
                    (monthlyDespesasFixas.reduce((acc, val) => acc + val, 0) +
                      monthlyDespesasVariaveis.reduce((acc, val) => acc + val, 0)),
                  currency
                )}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card
              sx={{
                background: darkMode
                  ? "linear-gradient(135deg, #37474F, #546E7A)"
                  : "linear-gradient(135deg, #E3F2FD, #BBDEFB)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography
                  variant="body1"
                  color={darkMode ? "#78909C" : "#1976D2"}
                  fontSize={{ xs: "0.788rem", sm: "1rem" }}
                >
                  Receitas
                </Typography>
                <MDBox
                  sx={{
                    backgroundColor: darkMode ? "#546E7A" : "#BBDEFB",
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TrendingUpRounded sx={{ fontSize: 16, color: darkMode ? "#BBDEFB" : "#2196F3" }} />
                </MDBox>
              </MDBox>
              <Typography
                variant="h5"
                fontWeight="bold"
                color={darkMode ? "#90CAF9" : "#1565C0"}
                fontSize={{ xs: "1.125rem", sm: "1.5rem" }}
              >
                {formatCurrency(
                  monthlyReceitas.reduce((acc, val) => acc + val, 0),
                  currency
                )}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card
              sx={{
                background: darkMode
                  ? "linear-gradient(135deg, #5D4037, #795548)"
                  : "linear-gradient(135deg, #FFEBEE, #EF9A9A)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography
                  variant="body1"
                  color={darkMode ? "#8D6E63" : "#C62828"}
                  fontSize={{ xs: "0.788rem", sm: "1rem" }}
                >
                  Despesas Fixas
                </Typography>
                <MDBox
                  sx={{
                    backgroundColor: darkMode ? "#795548" : "#EF9A9A",
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ReceiptLongRounded sx={{ fontSize: 16, color: darkMode ? "#FFAB91" : "#E53935" }} />
                </MDBox>
              </MDBox>
              <Typography
                variant="h5"
                fontWeight="bold"
                color={darkMode ? "#EF9A9A" : "#B71C1C"}
                fontSize={{ xs: "1.125rem", sm: "1.5rem" }}
              >
                {formatCurrency(
                  monthlyDespesasFixas.reduce((acc, val) => acc + val, 0),
                  currency
                )}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card
              sx={{
                background: darkMode
                  ? "linear-gradient(135deg, #4A2F4F, #6A1B9A)"
                  : "linear-gradient(135deg, #F3E5F5, #E1BEE7)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography
                  variant="body1"
                  color={darkMode ? "#AB47BC" : "#6A1B9A"}
                  fontSize={{ xs: "0.72rem", sm: "1rem" }}
                  sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  Despesas Variáveis
                </Typography>
                <MDBox
                  sx={{
                    backgroundColor: darkMode ? "#6A1B9A" : "#E1BEE7",
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ShoppingBagRounded sx={{ fontSize: 16, color: darkMode ? "#E1BEE7" : "#8E24AA" }} />
                </MDBox>
              </MDBox>
              <Typography
                variant="h5"
                fontWeight="bold"
                color={darkMode ? "#CE93D8" : "#4A148C"}
                fontSize={{ xs: "1.125rem", sm: "1.5rem" }}
              >
                {formatCurrency(
                  monthlyDespesasVariaveis.reduce((acc, val) => acc + val, 0),
                  currency
                )}
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Gráficos Mensais */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} md={6} lg={4}>
            <Card
              sx={{
                backgroundColor: darkMode ? "#212121" : "#FFF",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <Typography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                Receitas Mensais
              </Typography>
              <Typography variant="body2" color={darkMode ? "#B0BEC5" : "#6c757d"} mb={2}>
                Receitas acumuladas por mês
              </Typography>
              <MDBox display="flex" height={180}>
                {/* Eixo Y (Valores) */}
                <MDBox display="flex" flexDirection="column" justifyContent="space-between" height={120} mr={1}>
                  {generateYAxisLabels(Math.max(...monthlyReceitas, 1)).map((label, index) => (
                    <Typography
                      key={index}
                      variant="caption"
                      color={darkMode ? "#B0BEC5" : "#6c757d"}
                      sx={{ textAlign: "right", fontSize: "0.75rem" }}
                    >
                      {label}
                    </Typography>
                  ))}
                </MDBox>

                {/* Barras e Eixo X (Meses) */}
                <MDBox flex={1}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="flex-end" height={120}>
                    {monthlyReceitas.map((value, index) => {
                      const maxValue = Math.max(...monthlyReceitas, 1);
                      const height = (value / maxValue) * 100;

                      return (
                        <Tooltip key={index} title={formatCurrency(value, currency)} placement="top">
                          <MDBox display="flex" flexDirection="column" alignItems="center" sx={{ width: "8%" }}>
                            <MDBox
                              sx={{
                                width: 10,
                                height: `${height}px`,
                                backgroundColor: "#42A5F5",
                                borderRadius: "4px",
                                cursor: "pointer",
                              }}
                            />
                          </MDBox>
                        </Tooltip>
                      );
                    })}
                  </MDBox>
                  {/* Eixo X (Meses) */}
                  <MDBox display="flex" justifyContent="space-between" mt={1}>
                    {meses.map((mes, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        color={darkMode ? "#B0BEC5" : "#6c757d"}
                        sx={{ width: "8%", textAlign: "center", fontSize: "0.75rem" }}
                      >
                        {window.innerWidth <= 600 ? mes.charAt(0) : mes}
                      </Typography>
                    ))}
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Card
              sx={{
                backgroundColor: darkMode ? "#212121" : "#FFF",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <Typography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                Despesas Fixas Mensais
              </Typography>
              <Typography variant="body2" color={darkMode ? "#B0BEC5" : "#6c757d"} mb={2}>
                Despesas fixas acumuladas por mês
              </Typography>
              <MDBox display="flex" height={180}>
                {/* Eixo Y (Valores) */}
                <MDBox display="flex" flexDirection="column" justifyContent="space-between" height={120} mr={1}>
                  {generateYAxisLabels(Math.max(...monthlyDespesasFixas, 1)).map((label, index) => (
                    <Typography
                      key={index}
                      variant="caption"
                      color={darkMode ? "#B0BEC5" : "#6c757d"}
                      sx={{ textAlign: "right", fontSize: "0.75rem" }}
                    >
                      {label}
                    </Typography>
                  ))}
                </MDBox>

                {/* Barras e Eixo X (Meses) */}
                <MDBox flex={1}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="flex-end" height={120}>
                    {monthlyDespesasFixas.map((value, index) => {
                      const maxValue = Math.max(...monthlyDespesasFixas, 1);
                      const height = (value / maxValue) * 100;

                      return (
                        <Tooltip key={index} title={formatCurrency(value, currency)} placement="top">
                          <MDBox display="flex" flexDirection="column" alignItems="center" sx={{ width: "8%" }}>
                            <MDBox
                              sx={{
                                width: 10,
                                height: `${height}px`,
                                backgroundColor: "#EF5350",
                                borderRadius: "4px",
                                cursor: "pointer",
                              }}
                            />
                          </MDBox>
                        </Tooltip>
                      );
                    })}
                  </MDBox>
                  {/* Eixo X (Meses) */}
                  <MDBox display="flex" justifyContent="space-between" mt={1}>
                    {meses.map((mes, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        color={darkMode ? "#B0BEC5" : "#6c757d"}
                        sx={{ width: "8%", textAlign: "center", fontSize: "0.75rem" }}
                      >
                        {window.innerWidth <= 600 ? mes.charAt(0) : mes}
                      </Typography>
                    ))}
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Card
              sx={{
                backgroundColor: darkMode ? "#212121" : "#FFF",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <Typography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                Despesas Variáveis Mensais
              </Typography>
              <Typography variant="body2" color={darkMode ? "#B0BEC5" : "#6c757d"} mb={2}>
                Despesas variáveis acumuladas por mês
              </Typography>
              <MDBox display="flex" height={180}>
                {/* Eixo Y (Valores) */}
                <MDBox display="flex" flexDirection="column" justifyContent="space-between" height={120} mr={1}>
                  {generateYAxisLabels(Math.max(...monthlyDespesasVariaveis, 1)).map((label, index) => (
                    <Typography
                      key={index}
                      variant="caption"
                      color={darkMode ? "#B0BEC5" : "#6c757d"}
                      sx={{ textAlign: "right", fontSize: "0.75rem" }}
                    >
                      {label}
                    </Typography>
                  ))}
                </MDBox>

                {/* Barras e Eixo X (Meses) */}
                <MDBox flex={1}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="flex-end" height={120}>
                    {monthlyDespesasVariaveis.map((value, index) => {
                      const maxValue = Math.max(...monthlyDespesasVariaveis, 1);
                      const height = (value / maxValue) * 100;

                      return (
                        <Tooltip key={index} title={formatCurrency(value, currency)} placement="top">
                          <MDBox display="flex" flexDirection="column" alignItems="center" sx={{ width: "8%" }}>
                            <MDBox
                              sx={{
                                width: 10,
                                height: `${height}px`,
                                backgroundColor: "#AB47BC",
                                borderRadius: "4px",
                                cursor: "pointer",
                              }}
                            />
                          </MDBox>
                        </Tooltip>
                      );
                    })}
                  </MDBox>
                  {/* Eixo X (Meses) */}
                  <MDBox display="flex" justifyContent="space-between" mt={1}>
                    {meses.map((mes, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        color={darkMode ? "#B0BEC5" : "#6c757d"}
                        sx={{ width: "8%", textAlign: "center", fontSize: "0.75rem" }}
                      >
                        {window.innerWidth <= 600 ? mes.charAt(0) : mes}
                      </Typography>
                    ))}
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Lançamentos Pendentes e Despesas por Categoria */}
        <Grid container spacing={3} mt={2}>
          {/* === Lançamentos Pendentes === */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                backgroundColor: darkMode ? "#212121" : "#FFF",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                borderRadius: "12px",
                p: 2,
                height: { xs: "380px", sm: "355px" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography
                  variant="h6"
                  fontWeight="medium"
                  color={darkMode ? "white" : "dark"}
                  fontSize={{ xs: "1rem", sm: "1.25rem" }}
                >
                  Lançamentos Pendentes
                </Typography>

                {/* Botão de "ver mais" (ícone) */}
                <IconButton
                  aria-label="Ver mais lançamentos pendentes"
                  onClick={() => setIsResumoModalOpen(true)}
                  sx={{ color: darkMode ? "#90CAF9" : "#1976D2" }}
                >
                  <MoreHoriz />
                </IconButton>
              </MDBox>

              <MDBox flex={1} sx={{ overflowY: "auto" }}>
                {pendentesDetalhes.length > 0 ? (
                  pendentesDetalhes
                    .sort(
                      (a, b) =>
                        new Date(a.date.split("/").reverse().join("-")) -
                        new Date(b.date.split("/").reverse().join("-"))
                    )
                    .slice(0, 3)
                    .map((transacao, index) => {
                      const dataAtual = new Date();
                      const dataTransacao = new Date(transacao.date.split("/").reverse().join("-"));
                      dataTransacao.setMinutes(dataTransacao.getMinutes() + dataTransacao.getTimezoneOffset());
                      dataTransacao.setHours(0, 0, 0, 0);
                      dataAtual.setHours(0, 0, 0, 0);

                      let color = darkMode ? "#fff" : "#000";
                      let status = "Próximo";
                      let statusBgColor = darkMode ? "#4CAF50" : "#E8F5E9";
                      if (dataTransacao < dataAtual) {
                        color = "#D32F2F";
                        status = "Expirado";
                        statusBgColor = darkMode ? "#D32F2F" : "#FFEBEE";
                      } else if (dataTransacao.getTime() === dataAtual.getTime()) {
                        color = "#F57C00";
                        status = "Hoje";
                        statusBgColor = darkMode ? "#F57C00" : "#FFF3E0";
                      } else if (
                        dataTransacao > dataAtual &&
                        dataTransacao <= new Date(dataAtual.getTime() + 7 * 24 * 60 * 60 * 1000)
                      ) {
                        color = "#388E3C";
                        status = "Próximo";
                        statusBgColor = darkMode ? "#388E3C" : "#E8F5E9";
                      }

                      return (
                        <MDBox
                          key={index}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          py={1}
                          borderBottom={index < 2 ? `1px solid ${darkMode ? "#444" : "#e0e0e0"}` : "none"}
                        >
                          <MDBox display="flex" alignItems="center">
                            <Typography variant="body2" mr={1} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                              {transacao.emoji}
                            </Typography>
                            <MDBox>
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                color={darkMode ? "white" : "dark"}
                                fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
                              >
                                {transacao.description}
                              </Typography>
                              <Typography
                                variant="caption"
                                color={darkMode ? "#B0BEC5" : "#6c757d"}
                                fontSize={{ xs: "0.625rem", sm: "0.75rem" }}
                              >
                                {transacao.date}
                              </Typography>
                            </MDBox>
                          </MDBox>
                          <MDBox textAlign="right">
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color={color}
                              fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
                            >
                              {formatCurrency(parseCurrency(transacao.amount), currency)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                backgroundColor: statusBgColor,
                                color: darkMode ? "#fff" : color,
                                borderRadius: "12px",
                                px: 1,
                                py: 0.5,
                              }}
                              fontSize={{ xs: "0.625rem", sm: "0.75rem" }}
                            >
                              {status}
                            </Typography>
                          </MDBox>
                        </MDBox>
                      );
                    })
                ) : (
                  <Typography
                    variant="body2"
                    color={darkMode ? "#B0BEC5" : "#6c757d"}
                    textAlign="center"
                    fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
                  >
                    Nenhum lançamento pendente.
                  </Typography>
                )}
              </MDBox>

              {/* Removido o botão "Ver todos" do rodapé */}
            </Card>
          </Grid>

          {/* === Despesas por Categoria === */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                backgroundColor: darkMode ? "#212121" : "#FFF",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                borderRadius: "12px",
                p: 2,
                height: { xs: "380px", sm: "355px" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography
                  variant="h6"
                  fontWeight="medium"
                  color={darkMode ? "white" : "dark"}
                  fontSize={{ xs: "1rem", sm: "1.25rem" }}
                >
                  Despesas por Categoria
                </Typography>

                {/* Botão de "ver mais" (ícone) */}
                <IconButton
                  aria-label="Ver todas as despesas por categoria"
                  onClick={() => setIsGastosModalOpen(true)}
                  sx={{ color: darkMode ? "#90CAF9" : "#1976D2" }}
                >
                  <MoreHoriz />
                </IconButton>
              </MDBox>

              <MDBox display="flex" flex={1}>
                {/* Gráfico de Pizza (Apenas para Web - sm e acima) */}
                <MDBox
                  sx={{
                    width: { xs: "0%", sm: "50%" },
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                    justifyContent: "center",
                    mr: { xs: 0, sm: 2 },
                    position: "relative",
                  }}
                >
                  <MDBox
                    sx={{
                      position: "relative",
                      width: { xs: 80, sm: 140 },
                      height: { xs: 80, sm: 140 },
                    }}
                  >
                    {/* Gráfico de Pizza com conic-gradient */}
                    <MDBox
                      sx={{
                        width: "100%",
                        height: "100%",
                        background: `conic-gradient(${gradient})`,
                        borderRadius: "50%",
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }}
                    />
                    {/* Buraco central */}
                    <MDBox
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "70%",
                        height: "70%",
                        backgroundColor: darkMode ? "#212121" : "#F5F5F5",
                        borderRadius: "50%",
                      }}
                    />
                    {/* SVG para interatividade */}
                    <svg
                      style={{
                        position: "absolute",
                        top: 0,
                        left: "0",
                        width: "100%",
                        height: "100%",
                      }}
                      viewBox="0 0 100 100"
                    >
                      {sections.map((section, index) => (
                        <path
                          key={index}
                          d={describeArc(50, 50, 50, 35, section.startAngle, section.endAngle)}
                          fill="transparent"
                          onMouseEnter={() => setHoveredSection(index)}
                          onMouseLeave={() => setHoveredSection(null)}
                          style={{ cursor: "pointer" }}
                        />
                      ))}
                    </svg>
                    {/* Tooltip */}
                    {hoveredSection !== null && (
                      <Tooltip
                        title={`${sections[hoveredSection].category}: ${sections[hoveredSection].percentage}%`}
                        open={true}
                        placement="top"
                      >
                        <MDBox
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                          }}
                        />
                      </Tooltip>
                    )}
                  </MDBox>
                </MDBox>

                {/* Barras de Progresso (Ajustado para ocupar espaço total em Mobile) */}
                <MDBox sx={{ width: { xs: "100%", sm: "50%" } }}>
                  {gastosCategoria.length > 0 ? (
                    gastosCategoria.slice(0, 3).map((gasto, index) => (
                      <MDBox key={index} mb={{ xs: 1, sm: 1.5 }}>
                        {/* Layout para Web (sm e acima) */}
                        <MDBox sx={{ display: { xs: "none", sm: "block" } }}>
                          <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            {/* Emoji e Categoria */}
                            <MDBox display="flex" alignItems="center">
                              <Typography variant="body2" sx={{ mr: 0.5 }} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                                {gasto.emoji}
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                color={darkMode ? "white" : "dark"}
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  mr: 1,
                                }}
                                fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
                              >
                                {gasto.category}
                              </Typography>
                            </MDBox>
                            {/* Valor (alinhado com o final da barra de progresso) */}
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color={darkMode ? "white" : "dark"}
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
                            >
                              {formatCurrency(gasto.amount, currency)}
                            </Typography>
                          </MDBox>
                          {/* Barra de Progresso */}
                          <MDBox
                            sx={{
                              height: "12px",
                              backgroundColor: darkMode ? "#444" : "#e0e0e0",
                              borderRadius: "4px",
                              overflow: "hidden",
                              position: "relative",
                            }}
                          >
                            <MDBox
                              sx={{
                                height: "100%",
                                width: `${(gasto.amount / maxAmount) * 100}%`,
                                backgroundColor: sections[index]?.color || "#4CAF50",
                                borderRadius: "4px",
                              }}
                            />
                          </MDBox>
                          {/* Percentual (abaixo da barra, alinhado à direita) */}
                          <MDBox display="flex" justifyContent="flex-end">
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color={darkMode ? "#B0BEC5" : "#6c757d"}
                              fontSize={{ xs: "0.625rem", sm: "0.75rem" }}
                            >
                              {((gasto.amount / (despesasFixas + despesasVariaveis)) * 100).toFixed(1)}%
                            </Typography>
                          </MDBox>
                        </MDBox>

                        {/* Layout para Mobile (xs) */}
                        <MDBox sx={{ display: { xs: "block", sm: "none" } }}>
                          {/* Linha 1: Emoji e Categoria */}
                          <MDBox display="flex" alignItems="center" mb={0.5}>
                            <Typography variant="body2" sx={{ mr: 0.5 }} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                              {gasto.emoji}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color={darkMode ? "white" : "dark"}
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
                            >
                              {gasto.category}
                            </Typography>
                          </MDBox>
                          {/* Linha 2: Valor */}
                          <MDBox display="flex" justifyContent="flex-end" mb={0.5}>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color={darkMode ? "white" : "dark"}
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
                            >
                              {formatCurrency(gasto.amount, currency)}
                            </Typography>
                          </MDBox>
                          {/* Linha 3: Barra de Progresso */}
                          <MDBox
                            sx={{
                              height: "12px",
                              backgroundColor: darkMode ? "#444" : "#e0e0e0",
                              borderRadius: "4px",
                              overflow: "hidden",
                              position: "relative",
                              mb: 0.5,
                            }}
                          >
                            <MDBox
                              sx={{
                                height: "100%",
                                width: `${(gasto.amount / maxAmount) * 100}%`,
                                backgroundColor: sections[index]?.color || "#4CAF50",
                                borderRadius: "4px",
                              }}
                            />
                          </MDBox>
                          {/* Linha 4: Percentual */}
                          <MDBox display="flex" justifyContent="flex-end">
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color={darkMode ? "#B0BEC5" : "#6c757d"}
                              sx={{
                                whiteSpace: "nowrap",
                              }}
                              fontSize={{ xs: "0.625rem", sm: "0.75rem" }}
                            >
                              {((gasto.amount / (despesasFixas + despesasVariaveis)) * 100).toFixed(1)}%
                            </Typography>
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      color={darkMode ? "#B0BEC5" : "#6c757d"}
                      textAlign="center"
                      fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
                    >
                      Nenhum gasto registrado.
                    </Typography>
                  )}
                </MDBox>
              </MDBox>
              {/* Botão inferior removido conforme solicitado */}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Modal de Transações Pendentes */}
      <Dialog
        open={isResumoModalOpen}
        onClose={() => setIsResumoModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRadius: "12px",
            padding: "20px",
            boxShadow: theme.shadows[5],
          }),
        }}
      >
        <DialogTitle sx={{ pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <MDBox>
            <Typography
              variant="h6"
              fontWeight="medium"
              color={darkMode ? "white" : "dark"}
              fontSize={{ xs: "1rem", sm: "1.25rem" }}
            >
              Transações Pendentes
            </Typography>
            <Typography
              variant="body2"
              color={darkMode ? "#B0BEC5" : "#6c757d"}
              fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
            >
              Revise e gerencie seus pagamentos futuros
            </Typography>
          </MDBox>
          <IconButton onClick={() => setIsResumoModalOpen(false)} sx={{ color: darkMode ? "white" : "dark" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {pendentesDetalhes.length > 0 ? (
            pendentesDetalhes
              .sort(
                (a, b) =>
                  new Date(a.date.split("/").reverse().join("-")) - new Date(b.date.split("/").reverse().join("-"))
              )
              .map((transacao, index) => {
                const dataAtual = new Date();
                const dataTransacao = new Date(transacao.date.split("/").reverse().join("-"));
                dataTransacao.setMinutes(dataTransacao.getMinutes() + dataTransacao.getTimezoneOffset());
                dataTransacao.setHours(0, 0, 0, 0);
                dataAtual.setHours(0, 0, 0, 0);

                let color = darkMode ? "#fff" : "#000";
                let status = "Próximo";
                let statusBgColor = darkMode ? "#388E3C" : "#E8F5E9";
                if (dataTransacao < dataAtual) {
                  color = "#D32F2F";
                  status = "Expirado";
                  statusBgColor = darkMode ? "#D32F2F" : "#FFEBEE";
                } else if (dataTransacao.getTime() === dataAtual.getTime()) {
                  color = "#F57C00";
                  status = "Hoje";
                  statusBgColor = darkMode ? "#F57C00" : "#FFF3E0";
                } else if (
                  dataTransacao > dataAtual &&
                  dataTransacao <= new Date(dataAtual.getTime() + 7 * 24 * 60 * 60 * 1000)
                ) {
                  color = "#388E3C";
                  status = "Próximo";
                  statusBgColor = darkMode ? "#388E3C" : "#E8F5E9";
                }

                return (
                  <MDBox
                    key={index}
                    p={2}
                    mb={1}
                    sx={{
                      backgroundColor: darkMode ? "#333" : "#fff",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Linha Superior: Emoji, Descrição, Valor e Status */}
                    <MDBox
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "auto 1fr auto", sm: "auto 1fr auto auto" },
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {/* Coluna 1: Emoji */}
                      <MDBox display="flex" alignItems="center">
                        <Typography variant="body2" mr={1} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                          {transacao.emoji}
                        </Typography>
                      </MDBox>

                      {/* Coluna 2: Descrição e Data */}
                      <MDBox>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color={darkMode ? "white" : "dark"}
                          sx={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            overflow: "hidden",
                          }}
                          fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
                        >
                          {transacao.description}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={darkMode ? "#B0BEC5" : "#6c757d"}
                          fontSize={{ xs: "0.625rem", sm: "0.75rem" }}
                        >
                          {transacao.date}
                        </Typography>
                      </MDBox>

                      {/* Coluna 3: Valor e Status */}
                      <MDBox
                        textAlign="right"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          minWidth: "80px",
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color={darkMode ? "white" : "dark"}
                          sx={{
                            whiteSpace: "nowrap",
                          }}
                          fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
                        >
                          {formatCurrency(parseCurrency(transacao.amount), currency)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor: statusBgColor,
                            color: darkMode ? "#fff" : color,
                            borderRadius: "12px",
                            px: 1,
                            py: 0.5,
                            whiteSpace: "nowrap",
                          }}
                          fontSize={{ xs: "0.625rem", sm: "0.75rem" }}
                        >
                          {status}
                        </Typography>
                      </MDBox>

                      {/* Coluna 4: Botão e Texto "Marcar como pago" (somente em sm e acima) */}
                      <MDBox
                        sx={{
                          display: { xs: "none", sm: "flex" },
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "80px",
                        }}
                      >
                        <IconButton
                          onClick={() => handleMarkAsPaid(transacao.id)}
                          sx={{
                            backgroundColor: darkMode ? "#4A6572" : "#E3F2FD",
                            border: darkMode ? "1px solid #627D8A" : "1px solid #BBDEFB",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            mb: 0.5,
                          }}
                        >
                          <CheckCircleOutlineIcon
                            sx={{
                              fontSize: 16,
                              color: darkMode ? "#90CAF9" : "#42A5F5",
                            }}
                          />
                        </IconButton>
                        <Typography
                          variant="caption"
                          color={darkMode ? "white" : "dark"}
                          onClick={() => handleMarkAsPaid(transacao.id)}
                          sx={{
                            cursor: "pointer",
                            fontSize: "0.75rem",
                            textAlign: "center",
                            maxWidth: "80px",
                            whiteSpace: "normal",
                          }}
                          fontSize={{ xs: "0.625rem", sm: "0.75rem" }}
                        >
                          Marcar como pago
                        </Typography>
                      </MDBox>
                    </MDBox>

                    {/* Linha Inferior: Botão e Texto "Marcar como pago" (somente em xs) */}
                    <MDBox
                      sx={{
                        display: { xs: "flex", sm: "none" },
                        justifyContent: "center",
                        alignItems: "center",
                        mt: 1,
                      }}
                    >
                      <IconButton
                        onClick={() => handleMarkAsPaid(transacao.id)}
                        sx={{
                          backgroundColor: darkMode ? "#4A6572" : "#E3F2FD",
                          border: darkMode ? "1px solid #627D8A" : "1px solid #BBDEFB",
                          borderRadius: "50%",
                          width: 24,
                          height: 24,
                          mr: 1,
                        }}
                      >
                        <CheckCircleOutlineIcon
                          sx={{
                            fontSize: 16,
                            color: darkMode ? "#90CAF9" : "#42A5F5",
                          }}
                        />
                      </IconButton>
                      <Typography
                        variant="caption"
                        color={darkMode ? "white" : "dark"}
                        onClick={() => handleMarkAsPaid(transacao.id)}
                        sx={{
                          cursor: "pointer",
                          fontSize: "0.75rem",
                          textAlign: "center",
                        }}
                        fontSize={{ xs: "0.625rem", sm: "0.75rem" }}
                      >
                        Marcar como pago
                      </Typography>
                    </MDBox>
                  </MDBox>
                );
              })
          ) : (
            <Typography
              style={{ color: darkMode ? "#B0BEC5" : "#6c757d" }}
              fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
            >
              Nenhuma transação pendente.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes da Transação */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseTransactionModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRadius: "12px",
            padding: "20px",
            boxShadow: theme.shadows[5],
          }),
        }}
      >
        <DialogTitle sx={{ color: darkMode ? "white" : "dark" }} fontSize={{ xs: "1rem", sm: "1.25rem" }}>
          Detalhes da Transação
        </DialogTitle>
        <DialogContent>
          {selectedTransaction ? (
            <>
              <Typography color={darkMode ? "white" : "dark"} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                <strong>Data:</strong> {selectedTransaction.date}
              </Typography>
              <Typography color={darkMode ? "white" : "dark"} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                <strong>Categoria:</strong> {selectedTransaction.description}
              </Typography>
              <Typography color={darkMode ? "white" : "dark"} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                <strong>Forma de Pagamento:</strong> {selectedTransaction.paymentMethod}
              </Typography>
              <Typography color={darkMode ? "white" : "dark"} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                <strong>Valor:</strong> {formatCurrency(parseCurrency(selectedTransaction.amount), currency)}
              </Typography>
              <Typography color={darkMode ? "white" : "dark"} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                <strong>Descrição:</strong> {selectedTransaction.observation}
              </Typography>
              <Typography color={darkMode ? "white" : "dark"} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                <strong>Status:</strong> {selectedTransaction.paid ? "Pago" : "Pendente"}
              </Typography>
            </>
          ) : (
            <Typography color={darkMode ? "white" : "dark"} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
              Carregando...
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={handleCloseTransactionModal}
            color="secondary"
            fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
          >
            Cancelar
          </MDButton>
          <MDButton
            onClick={async () => {
              try {
                const transactionRef = doc(db, "usuarios", userId, "lancamentos", selectedTransaction.id);
                await updateDoc(transactionRef, { pago: true });

                setPendentesDetalhes((prevPendentes) =>
                  prevPendentes.filter((transacao) => transacao.id !== selectedTransaction.id)
                );

                setSelectedTransaction(null);
                setIsModalOpen(false);
              } catch (error) {
                console.error("Erro ao marcar transação como paga:", error);
              }
            }}
            color="info"
            fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
          >
            Marcar como Pago
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Modal de Despesas por Categoria */}
      <Dialog
        open={isGastosModalOpen}
        onClose={() => setIsGastosModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRadius: "12px",
            padding: "20px",
            boxShadow: theme.shadows[5],
            top: "10%",
          }),
        }}
      >
        <DialogTitle sx={{ pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <MDBox>
            <Typography
              variant="h6"
              fontWeight="medium"
              color={darkMode ? "white" : "dark"}
              fontSize={{ xs: "1rem", sm: "1.25rem" }}
            >
              Despesas por Categoria
            </Typography>
            <Typography
              variant="body2"
              color={darkMode ? "#B0BEC5" : "#6c757d"}
              fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
            >
              Distribuição de despesas
            </Typography>
          </MDBox>
          <IconButton onClick={() => setIsGastosModalOpen(false)} sx={{ color: darkMode ? "white" : "dark" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            {gastosCategoria.length > 0 ? (
              gastosCategoria.map((gasto, index) => (
                <MDBox key={index} display="flex" alignItems="center" mb={1}>
                  {/* Emoji */}
                  <Typography variant="body2" sx={{ mr: 1 }} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
                    {gasto.emoji}
                  </Typography>
                  {/* Nome da Categoria */}
                  <Typography
                    variant="body2"
                    color={darkMode ? "white" : "dark"}
                    sx={{ width: "90px", mr: 1 }}
                    fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
                  >
                    {gasto.category}
                  </Typography>
                  {/* Barra de Progresso */}
                  <MDBox
                    sx={{
                      flex: 1,
                      height: "12px",
                      backgroundColor: darkMode ? "#444" : "#e0e0e0",
                      borderRadius: "4px",
                      overflow: "hidden",
                      mr: 2,
                    }}
                  >
                    <MDBox
                      sx={{
                        height: "100%",
                        width: `${(gasto.amount / maxAmount) * 100}%`,
                        backgroundColor:
                          index === 0
                            ? "#F57C00"
                            : index === 1
                            ? "#8E24AA"
                            : index === 2
                            ? "#AB47BC"
                            : index === 3
                            ? "#4CAF50"
                            : index === 4
                            ? "#FF9800"
                            : "#7B1FA2",
                        borderRadius: "4px",
                      }}
                    />
                  </MDBox>
                  {/* Valor */}
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color={darkMode ? "white" : "dark"}
                    sx={{ width: "90px", textAlign: "right", mr: 2 }}
                    fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
                  >
                    {formatCurrency(gasto.amount, currency)}
                  </Typography>
                  {/* Percentual */}
                  <Typography
                    variant="body2"
                    color={darkMode ? "#B0BEC5" : "#6c757d"}
                    sx={{ width: "50px", textAlign: "right" }}
                    fontSize={{ xs: "0.625rem", sm: "0.75rem" }}
                  >
                    {((gasto.amount / (despesasFixas + despesasVariaveis)) * 100).toFixed(1)}%
                  </Typography>
                </MDBox>
              ))
            ) : (
              <Typography
                variant="body2"
                color={darkMode ? "#B0BEC5" : "#6c757d"}
                textAlign="center"
                fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
              >
                Nenhum gasto registrado.
              </Typography>
            )}
          </MDBox>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default Dashboard;
